import { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { FileUploadField } from '@/modules/common/components/organisms/file-upload-field';
import { useProfileAvatarConfig } from '@/modules/settings/hooks/account/use-profile-avatar-config';
import { useUserProfile } from '@/modules/settings/hooks/account/use-user-profile';

import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { SettingsOutletContext } from '../../types/settings-footer';
import { ProfileInfoFormData, profileInfoSchema } from '../../validations/profile-info.validation';

export const ProfileInfoPage = () => {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const { setFooterState } = useOutletContext<SettingsOutletContext>();
  const { userProfile, isLoading, error, updateProfile, isUpdating } = useUserProfile();
  const avatarConfig = useProfileAvatarConfig(userProfile?.profileImageUrl ?? null);
  const avatarForm = useForm<{ avatar: File | null }>({ defaultValues: { avatar: null } });

  const form = useForm<ProfileInfoFormData>({
    resolver: zodResolver(profileInfoSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
  });

  const {
    formState: { isDirty, isValid },
  } = form;

  // Hook to guard against navigation when there are unsaved changes
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty,
    onDiscard: () => form.reset(),
  });

  // Update form when user profile loads
  useEffect(() => {
    if (userProfile) {
      form.reset({
        name: userProfile.name,
      });
    }
  }, [userProfile, form]);

  const onSubmit = useCallback(
    (data: ProfileInfoFormData) => {
      updateProfile({ name: data.name });
    },
    [updateProfile]
  );

  // Update footer state based on form state
  useEffect(() => {
    setFooterState({
      isVisible: true,
      isEnabled: isDirty && isValid,
      isLoading: isUpdating,
      onSave: form.handleSubmit(onSubmit),
    });

    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [isDirty, isValid, isUpdating, setFooterState, form, onSubmit]);

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      unsavedChangesGuard={{
        isModalOpen: unsavedChangesGuard.isModalOpen,
        onDiscard: unsavedChangesGuard.handleModalDiscard,
        onCancel: unsavedChangesGuard.handleModalCancel,
      }}
    >
      {userProfile && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('pages.profileInfo.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Form {...form}>
                <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                  <FileUploadField
                    name="avatar"
                    control={avatarForm.control}
                    label={t('pages.profileInfo.fields.avatar.label')}
                    helpText={t('pages.profileInfo.fields.avatar.helpText')}
                    config={avatarConfig}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('pages.profileInfo.fields.name.label')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={t('pages.profileInfo.fields.name.placeholder')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormItem>
                    <FormLabel>{t('pages.profileInfo.fields.email.label')}</FormLabel>
                    <FormControl>
                      <Input value={userProfile.email} disabled className="bg-muted" />
                    </FormControl>
                  </FormItem>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      )}
    </SettingsPageWrapper>
  );
};
